import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import CruisePriceDetails from './CruisePriceDetails'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import CruisesUrgencyTags from 'components/Cruises/CruisesUrgencyTags'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import CruiseInclusionListHomeTile from 'components/Cruises/CruiseInclusion/CruiseInclusionListHomeTile'
import CruiseImageCornerLabels from 'components/SearchKit/SearchTiles/cruises/CruiseImageCornerLabels'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { useIntl } from 'react-intl'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import GrowthCarouselCardSmall from './GrowthOfferCard/GrowthCarouselCardSmall'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'
import { getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import GrowthCruiseInclusions from './GrowthOfferCard/GrowthCruiseInclusions'
import useGrowthOfferTile from './GrowthOfferCard/useGrowthOfferTile'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.CruiseOffer
  onClick: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  filters?: App.OfferListFilters
}

function CruiseOfferCard(props: Props) {
  const isGrowthTile = useGrowthOfferTile()
  const intl = useIntl()
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
    filters,
  } = props

  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusions)
  const view = useCruiseOfferView({ offer, filters })
  const {
    luxPlusInclusions,
    evergreenInclusions,
    tileInclusions,
    standardInclusions,
    memberLowestPrice,
    memberPriceType,
    isPriceLoading,
    canSeeLuxPlusLabel,
    lowestPrice,
  } = view

  const priceDetails = useMemo<App.Pricing>(() => {
    const displayedPrice = lowestPrice.promotionalPrice ?? lowestPrice.primaryPrice

    if (displayedPrice.value !== undefined && displayedPrice.discountPercent !== undefined) {
      return {
        price: displayedPrice.total,
        saleUnit: displayedPrice.type,
        fees: [],
        value: displayedPrice.value,
        discountPercent: displayedPrice.discountPercent,
      }
    }

    return {
      price: displayedPrice.total,
      saleUnit: displayedPrice.type,
      fees: [],
    }
  }, [lowestPrice])
  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])
  const tileBurn = useSearchOfferBurnPricing({
    price: priceDetails.price,
    memberPrice: memberLowestPrice?.primaryPrice.total,
    loyaltyProductType,
    viewPriceInPoints: filters?.viewPriceInPoints,
  })

  const inclusionsElement = useMemo(() => (
    <CruiseInclusionListHomeTile
      evergreenInclusions={evergreenInclusions}
      tileInclusions={tileInclusions}
      luxPlusInclusions={luxPlusInclusions}
      standardInclusions={standardInclusions}
      canSeeLuxPlusLabel={canSeeLuxPlusLabel}
      offer={offer}
    />
  ), [canSeeLuxPlusLabel, evergreenInclusions, offer, tileInclusions, standardInclusions, luxPlusInclusions])

  const imageCornerLabel = <CruiseImageCornerLabels view={view} countdownTagPlace="offer-card" />

  if (isGrowthTile) {
    const growthInclusionsElement = <GrowthCruiseInclusions
      evergreenInclusions={evergreenInclusions}
      tileInclusions={tileInclusions}
      standardInclusions={standardInclusions}
    />
    return (
      <ProductPaletteProvider product={offer}>
        <GrowthCarouselCardSmall
          variant={variant}
          className={className}
          eagerLoadImage={eagerLoadImage}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind={iconKind} iconVariant={iconVariant} />}
          image={offer.images[0]}
          images={offer.images}
          location={getLocationString(intl, offer)}
          providerName={offer.cruiseLine.name}
          title={offer.name}
          to={offerUrl}
          onClick={onClick}
          urgencyLabels={<LabelGroup>
            {canSeeLuxPlusLabel && <CruiseLuxPlusLabel isModalEnabled offer={offer} />}
            {isEnabledLuxPlusCruiseInclusions && luxPlusInclusions.length > 0 && <CruiseLuxPlusOnboardCreditLabel luxPlusInclusions={luxPlusInclusions} offer={offer} />}
            {imageCornerLabel}
            <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
            <CruisesUrgencyTags tags={offer.urgencyTags} />
          </LabelGroup>}
          priceDetails={
            priceDetails && <CruisePriceDetails
              offerId={offer.id}
              priceDetails={priceDetails}
              memberPrice={memberLowestPrice?.primaryPrice.total}
              memberPriceType={memberPriceType}
              isPriceLoading={isPriceLoading}
              filters={filters}
              loyaltyProductType={loyaltyProductType}
              promotionalPrice={lowestPrice.promotionalPrice}
              cabinCategory={view.cabinCategory}
              discountAmount={view.discountPills?.discountPrice}
            />
          }
          taglineInclusions={growthInclusionsElement}
        />
      </ProductPaletteProvider>
    )
  }

  return (
    <ProductPaletteProvider product={offer}>
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={offer.images[0]}
        images={offer.images}
        title={offer.name}
        location={getLocationString(intl, offer)}
        to={offerUrl}
        providerName={offer.cruiseLine.name}
        onClick={onClick}
        urgencyLabels={<LabelGroup>
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          <CruisesUrgencyTags tags={offer.urgencyTags} />
        </LabelGroup>}
        priceDetails={
          priceDetails && <CruisePriceDetails
            offerId={offer.id}
            variant="condensed"
            priceDetails={priceDetails}
            memberPrice={memberLowestPrice?.primaryPrice.total}
            memberPriceType={memberPriceType}
            isPriceLoading={isPriceLoading}
            filters={filters}
            loyaltyProductType={loyaltyProductType}
            promotionalPrice={lowestPrice.promotionalPrice}
            cabinCategory={view.cabinCategory}
            discountAmount={view.discountPills?.discountPrice}
          />
        }
        loyaltyPointsElement={<CruiseLoyaltyPoints
          offer={offer}
          calculationType="estimate"
          pointsToApply={tileBurn.pointsToApplyForEarn}
          burnPriceInPoints={tileBurn.priceInPointsForEarn}
        />}
        taglineInclusions={inclusionsElement}
        imageCornerLabel={imageCornerLabel}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
        image={offer.images[0]}
        images={offer.images}
        location={getLocationString(intl, offer)}
        title={offer.name}
        to={offerUrl}
        onClick={onClick}
        urgencyLabels={<LabelGroup>
          <AgentHubAdditionalCommissionLabel offerId={offer?.id} />
          <CruisesUrgencyTags tags={offer.urgencyTags} />
        </LabelGroup>}
        priceDetails={
          priceDetails && <CruisePriceDetails
            offerId={offer.id}
            priceDetails={priceDetails}
            memberPrice={memberLowestPrice?.primaryPrice.total}
            memberPriceType={memberPriceType}
            isPriceLoading={isPriceLoading}
            filters={filters}
            loyaltyProductType={loyaltyProductType}
            promotionalPrice={lowestPrice.promotionalPrice}
            cabinCategory={view.cabinCategory}
            discountAmount={view.discountPills?.discountPrice}
          />
        }
        loyaltyPointsElement={<CruiseLoyaltyPoints
          offer={offer}
          calculationType="estimate"
          pointsToApply={tileBurn.pointsToApplyForEarn}
          burnPriceInPoints={tileBurn.priceInPointsForEarn}
        />}
        taglineInclusions={inclusionsElement}
        imageCornerLabel={imageCornerLabel}
      />}
    </ProductPaletteProvider>
  )
}

export default CruiseOfferCard
